import React, { useRef, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { MenuOptionWithIdNumber } from '@modernloop/shared/components';
import { TriangleDownIcon } from '@modernloop/shared/icons';
import { Button, Menu, MenuItem } from '@mui/material';

export enum NumberOfDays {
  Custom = -1,
  OneWeek = 7,
  TwoWeeks = 14,
  ThreeWeeks = 21,
  FourWeeks = 28,
  TwoMonths = 60,
}

export type TimeFramePickerProps = {
  timeFrame: NumberOfDays;
  onChange: (value: NumberOfDays) => void;
};

const TIMEFRAME_OPTION_TEST_IDS: Record<Exclude<NumberOfDays, NumberOfDays.Custom>, string> = {
  [NumberOfDays.OneWeek]: 'self-schedule-options-one-week',
  [NumberOfDays.TwoWeeks]: 'self-schedule-options-two-weeks',
  [NumberOfDays.ThreeWeeks]: 'self-schedule-options-three-weeks',
  [NumberOfDays.FourWeeks]: 'self-schedule-options-four-weeks',
  [NumberOfDays.TwoMonths]: 'self-schedule-options-two-months',
};

const TimeFramePicker = ({ timeFrame, onChange }: TimeFramePickerProps): JSX.Element => {
  const { t } = useTranslation();
  const [showDaysMenu, setShowDaysMenu] = useState(false);
  const daysButtonRef = useRef<HTMLButtonElement>(null);
  const timeRangeOptions: MenuOptionWithIdNumber[] = [
    { id: NumberOfDays.OneWeek, value: t('hiring-dashboard-schedule:schedule.selfScheduleOptions.timeframe.nextWeek') },
    {
      id: NumberOfDays.TwoWeeks,
      value: t('hiring-dashboard-interviewers:interviewers.pauseDates.next2Weeks'),
    },
    {
      id: NumberOfDays.ThreeWeeks,
      value: t('hiring-dashboard-schedule:schedule.selfScheduleOptions.timeframe.nextThreeWeeks'),
    },
    {
      id: NumberOfDays.FourWeeks,
      value: t('hiring-dashboard-interviewers:interviewers.pauseDates.next4Weeks'),
    },
    {
      id: NumberOfDays.TwoMonths,
      value: t('hiring-dashboard-schedule:schedule.selfScheduleOptions.timeframe.nextTwoMonths'),
    },
  ];

  const timeFrameString = timeRangeOptions.find((option) => option.id === timeFrame)?.value;

  const handleShowMenu = () => setShowDaysMenu(true);
  const handleHideMenu = () => setShowDaysMenu(false);
  const handleSelectTimeFrame = (option: MenuOptionWithIdNumber) => {
    if (!option.id || Number.isNaN(option.id)) {
      return;
    }
    onChange(option.id as NumberOfDays);
    handleHideMenu();
  };

  return (
    <>
      <Button
        data-testid="self-schedule-options-weeks-list"
        sx={{
          borderRadius: '6px',
          fontWeight: (theme) => theme.typography.fontWeightRegular,
          minWidth: '200px',
          display: 'flex',
          justifyContent: 'space-between',
        }}
        ref={daysButtonRef}
        endIcon={<TriangleDownIcon />}
        onClick={handleShowMenu}
      >
        {timeFrameString ?? t('hiring-dashboard-interviewers:interviewers.pauseDates.custom')}
      </Button>
      {showDaysMenu && (
        <Menu
          id="select-time-frame-menu"
          anchorEl={daysButtonRef.current}
          open={showDaysMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          onClose={handleHideMenu}
          sx={{
            width: daysButtonRef.current?.clientWidth ? daysButtonRef.current?.clientWidth - 16 : undefined,
          }}
        >
          {timeRangeOptions.map((option) => (
            <MenuItem
              data-testid={
                option.id === NumberOfDays.Custom
                  ? undefined
                  : TIMEFRAME_OPTION_TEST_IDS[option.id as Exclude<NumberOfDays, NumberOfDays.Custom>]
              }
              key={option.id}
              value={option.id}
              onClick={() => handleSelectTimeFrame(option)}
              selected={option.id === timeFrame}
            >
              {option.value}
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
};

export default TimeFramePicker;
